import React, { useState, useEffect, useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { getDocument } from "pdfjs-dist/build/pdf";
import { GlobalWorkerOptions } from "pdfjs-dist/build/pdf";
import { useTranslation } from "react-i18next";
import LazyImage from "../../components/common/LazyImage";

// Configure PDF.js worker
const pdfjsWorker = await import("pdfjs-dist/build/pdf.worker.min.js");
GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// Helper function to get localized category name
const getLocalizedCategoryName = (fullName, language) => {
  if (!fullName) return "";
  const translations = fullName.split("|");
  switch (language) {
    case "en":
      return translations[0] || "";
    case "fr":
      return translations[1] || translations[0] || "";
    case "ar":
      return translations[2] || translations[0] || "";
    default:
      return translations[0] || "";
  }
};

// MUI Components
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Alert,
  CircularProgress,
  Pagination,
  Menu,
  LinearProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Shield,
  Security,
  Verified,
} from "@mui/icons-material";
import { booksAPI, categoriesAPI, adminAPI } from "../../services/api";
import { useSnackbar } from "../../contexts/SnackbarContext";
import AccessRequestManager from "../../components/admin/AccessRequestManager";
import TabPanel from "../../components/admin/TabPanel";
import CustomContainer from "../../components/common/CustomContainer";

const getCoverImageUrl = (path) => {
  if (!path) return "/placeholder-cover.jpg";
  return path.startsWith("http")
    ? path
    : `${import.meta.env.VITE_API_URL}${path}`;
};
const DownloadDialog = ({ open, book, onClose }) => {
  const [downloadType, setDownloadType] = useState("full");
  const [sectionStart, setSectionStart] = useState("");
  const [sectionEnd, setSectionEnd] = useState("");
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const handleDownload = async () => {
    try {
      if (downloadType === "section") {
        if (!sectionStart || !sectionEnd) {
          showSnackbar(
            t("admin.bookManagement.download.validation.specifyPages"),
            "error"
          );
          return;
        }
        if (parseInt(sectionStart) > parseInt(sectionEnd)) {
          showSnackbar(
            t("admin.bookManagement.download.validation.invalidRange"),
            "error"
          );
          return;
        }
        if (parseInt(sectionEnd) > book.total_pages) {
          showSnackbar(
            t("admin.bookManagement.download.validation.exceedTotal"),
            "error"
          );
          return;
        }
        await booksAPI.download(book.id, sectionStart, sectionEnd);
      } else {
        await booksAPI.download(book.id);
      }
      onClose();
      showSnackbar(t("admin.bookManagement.download.success"), "success");
    } catch (error) {
      showSnackbar(t("admin.bookManagement.download.error"), "error");
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{t("admin.bookManagement.download.title")}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <RadioGroup
            value={downloadType}
            onChange={(e) => setDownloadType(e.target.value)}
          >
            <FormControlLabel
              value="full"
              control={<Radio />}
              label={t("admin.bookManagement.download.fullBook")}
            />
            <FormControlLabel
              value="section"
              control={<Radio />}
              label={t("admin.bookManagement.download.section")}
            />
          </RadioGroup>

          {downloadType === "section" && (
            <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
              <TextField
                label={t("admin.bookManagement.download.startPage")}
                type="number"
                value={sectionStart}
                onChange={(e) => setSectionStart(e.target.value)}
                inputProps={{ min: 1, max: book?.total_pages }}
              />
              <TextField
                label={t("admin.bookManagement.download.endPage")}
                type="number"
                value={sectionEnd}
                onChange={(e) => setSectionEnd(e.target.value)}
                inputProps={{ min: 1, max: book?.total_pages }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button onClick={handleDownload} variant="contained">
          {t("common.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const BookForm = ({
  book,
  onSubmit,
  onCancel,
  userSelectedCover,
  onUserSelectedCoverChange,
}) => {
  const [formData, setFormData] = useState({
    title: "",
    author: "",
    description: "",
    isbn: "",
    publication_year: "",
    language: "fr",
    category_id: "",
    total_pages: "",
    ...book,
  });

  const [coverFile, setCoverFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [maxPdfFileSize, setMaxPdfFileSize] = useState(50);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  // Separate security state for PDF and cover
  const [pdfSecurity, setPdfSecurity] = useState(null);
  const [coverSecurity, setCoverSecurity] = useState(null);
  const [isScanningPdf, setIsScanningPdf] = useState(false);
  const [isScanningCover, setIsScanningCover] = useState(false);
  const { showSnackbar } = useSnackbar();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    // Cleanup function for upload states
    return () => {
      setIsUploading(false);
      setUploadProgress(0);
      setPdfSecurity(null);
      setCoverSecurity(null);
      setIsScanningPdf(false);
      setIsScanningCover(false);
      onUserSelectedCoverChange(false);
    };
  }, [onUserSelectedCoverChange]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await adminAPI.getSettings();
        if (response.data.maxPdfFileSize) {
          setMaxPdfFileSize(parseInt(response.data.maxPdfFileSize));
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
        showSnackbar(t("admin.bookManagement.errors.loadSettings"), "error");
      }
    };

    fetchSettings();
  }, [showSnackbar]);

  const getPDFPageCount = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const typedarray = new Uint8Array(event.target.result);
          const pdf = await pdfjsLib.getDocument(typedarray).promise;
          console.log("PDF loaded:", pdf);
          resolve(pdf.numPages);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoriesAPI.getAll();
        const data = response.data?.categories || response.data || [];
        setCategories(Array.isArray(data) ? data : []);
      } catch (error) {
        showSnackbar(t("admin.bookManagement.errors.loadCategories"), "error");
      }
    };
    fetchCategories();
  }, [showSnackbar]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Scan file immediately when selected
  const scanFileImmediately = async (file, type) => {
    const setSecurity = type === 'pdf' ? setPdfSecurity : setCoverSecurity;
    const setScanning = type === 'pdf' ? setIsScanningPdf : setIsScanningCover;

    console.log(`Starting scan for ${type}:`, file.name);
    setScanning(true);
    setSecurity(null);

    const startTime = Date.now();

    try {
      const formData = new FormData();
      // Use the correct field name based on type (backend expects 'pdf' or 'cover')
      formData.append(type, file);

      const token = localStorage.getItem("token");
      console.log(`Uploading ${type} to scan endpoint...`);

      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      const response = await fetch(`${import.meta.env.VITE_API_URL}/books/scan-file`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      console.log(`Scan response for ${type}:`, response.status);
      const data = await response.json();
      console.log(`Scan data for ${type}:`, data);

      // Log response for debugging
      if (!response.ok) {
        console.error('Scan error:', data);
      }

      // Add minimum 1.5 second delay so user can see the scanning animation
      const elapsed = Date.now() - startTime;
      const minDelay = 1500; // 1.5 seconds
      if (elapsed < minDelay) {
        await new Promise(resolve => setTimeout(resolve, minDelay - elapsed));
      }

      if (data.security) {
        setSecurity(data.security);
      } else if (response.ok) {
        // If response is ok but no security data, assume file is clean
        setSecurity({
          scanned: false,
          scannerAvailable: false,
          message: 'Scan completed'
        });
      }

      setScanning(false);
    } catch (error) {
      console.error(`Error scanning ${type}:`, error);

      // Still wait minimum time even on error
      const elapsed = Date.now() - startTime;
      const minDelay = 1500;
      if (elapsed < minDelay) {
        await new Promise(resolve => setTimeout(resolve, minDelay - elapsed));
      }

      // Check if it was a timeout
      if (error.name === 'AbortError') {
        console.log(`Scan timeout for ${type}`);
        setSecurity({
          scanned: false,
          scannerAvailable: false,
          error: false,
          message: 'Scan timeout - file too large'
        });
      } else {
        setSecurity({
          scanned: false,
          error: true,
          message: 'Error scanning file'
        });
      }
      setScanning(false);
    }
  };

  const handleFileChange = useCallback(
    async (e, type) => {
      const file = e.target.files[0];
      if (!file) return;

      // Validate file type
      if (type === "cover" && !file.type.startsWith("image/")) {
        showSnackbar(
          t("admin.bookManagement.validation.coverFileType"),
          "error"
        );
        return;
      }
      if (type === "pdf" && file.type !== "application/pdf") {
        showSnackbar(t("admin.bookManagement.validation.pdfFileType"), "error");
        return;
      }

      // Validate file size
      const fileSizeMB = file.size / (1024 * 1024);
      if (type === "pdf" && fileSizeMB > maxPdfFileSize) {
        showSnackbar(
          t("admin.bookManagement.validation.pdfFileSize", {
            size: maxPdfFileSize,
          }),
          "error"
        );
        return;
      } else if (type === "cover" && file.size > 5 * 1024 * 1024) {
        // 5MB for cover images
        showSnackbar(
          t("admin.bookManagement.validation.coverFileSize"),
          "error"
        );
        return;
      }

      // Scan file immediately for viruses
      scanFileImmediately(file, type);

      if (type === "cover") {
        setCoverFile(file);
        onUserSelectedCoverChange(true);
      } else {
        try {
          setPdfFile(file);
          const pageCount = await getPDFPageCount(file);
          setFormData((prev) => ({
            ...prev,
            total_pages: pageCount,
          }));

          // Generate cover from first page if user hasn't explicitly selected a cover
          if (!userSelectedCover) {
            showSnackbar(
              t("admin.bookManagement.messages.generatingCover"),
              "info"
            );
            try {
              const generatedCover = await generateCoverFromPDF(file);
              setCoverFile(generatedCover);
              showSnackbar(
                t("admin.bookManagement.book.messages.pdfLoadedWithCover"),
                "success"
              );
            } catch (error) {
              console.error("Error generating cover:", error);
              showSnackbar(
                t("admin.bookManagement.book.messages.pdfLoadedNoCover"),
                "warning"
              );
            }
          } else {
            showSnackbar(
              t("admin.bookManagement.book.messages.pdfLoadSuccess", {
                pages: pageCount,
              }),
              "success"
            );
          }
        } catch (error) {
          showSnackbar(t("admin.bookManagement.book.errors.pdfRead"), "error");
          console.error("PDF reading error:", error);
          setPdfFile(null);
        }
      }
    },
    [
      showSnackbar,
      maxPdfFileSize,
      book,
      userSelectedCover,
      onUserSelectedCoverChange,
    ]
  );

  const generateCoverFromPDF = async (pdfFile) => {
    try {
      // Read the PDF file
      const arrayBuffer = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsArrayBuffer(pdfFile);
      });

      // Load the PDF document
      const typedArray = new Uint8Array(arrayBuffer);
      const pdf = await pdfjsLib.getDocument(typedArray).promise;

      // Get the first page
      const page = await pdf.getPage(1);

      // Set up canvas with a fixed size for thumbnail
      const scale = 2.0;
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d", { alpha: false });

      // Make the canvas size more manageable for a cover image
      const maxDimension = 800;
      const aspectRatio = viewport.width / viewport.height;

      if (viewport.width > viewport.height) {
        canvas.width = maxDimension;
        canvas.height = maxDimension / aspectRatio;
      } else {
        canvas.height = maxDimension;
        canvas.width = maxDimension * aspectRatio;
      }

      const scaledViewport = page.getViewport({
        scale: scale * (canvas.width / viewport.width),
      });

      // Set canvas dimensions
      canvas.height = canvas.height;
      canvas.width = canvas.width;

      // White background
      context.fillStyle = "white";
      context.fillRect(0, 0, canvas.width, canvas.height);

      // Render PDF page
      await page.render({
        canvasContext: context,
        viewport: scaledViewport,
        background: "white",
      }).promise;

      // Convert to file
      return new Promise((resolve, reject) => {
        try {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error("Failed to create image blob"));
                return;
              }
              const file = new File([blob], "cover.png", {
                type: "image/png",
                lastModified: Date.now(),
              });
              resolve(file);
            },
            "image/png",
            0.95
          );
        } catch (error) {
          reject(error);
        }
      });
    } catch (error) {
      console.error("Error generating cover:", error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      showSnackbar(
        t("admin.bookManagement.book.validation.titleRequired"),
        "error"
      );
      return;
    }
    if (!formData.author.trim()) {
      showSnackbar(
        t("admin.bookManagement.book.validation.authorRequired"),
        "error"
      );
      return;
    }
    if (!formData.category_id) {
      showSnackbar(
        t("admin.bookManagement.book.validation.categoryRequired"),
        "error"
      );
      return;
    }
    // PDF is now optional for initial creation
    // We now only enforce title, author and category as required fields

    // ISBN Validation if provided
    if (formData.isbn) {
      // Remove hyphens and spaces for validation
      const cleanIsbn = formData.isbn.replace(/[-\s]/g, "");

      // ISBN-13: 13 digits
      const isValidIsbn13 = /^(?:\d{13})$/.test(cleanIsbn);
      // ISBN-10: 10 digits or 9 digits followed by 'X'
      const isValidIsbn10 = /^(?:\d{9}[\dX])$/.test(cleanIsbn);

      if (!isValidIsbn10 && !isValidIsbn13) {
        showSnackbar(
          t("admin.bookManagement.book.validation.invalidIsbn"),
          "error"
        );
        return;
      }
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);
      const submitData = new FormData();

      // Add all form fields
      Object.keys(formData).forEach((key) => {
        if (
          formData[key] !== undefined &&
          formData[key] !== null &&
          formData[key] !== ""
        ) {
          submitData.append(key, formData[key]);
        }
      });

      if (pdfFile) {
        submitData.append("pdf", pdfFile);
      }

      if (coverFile) {
        submitData.append("cover", coverFile);
      }

      // Create custom XMLHttpRequest to track upload progress
      const xhr = new XMLHttpRequest();
      const promise = new Promise((resolve, reject) => {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded * 100) / event.total);
            setUploadProgress(progress);
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.response));
          } else {
            reject(new Error(`HTTP Error: ${xhr.status}`));
          }
        };

        xhr.onerror = () => reject(new Error("Network Error"));
      });

      const baseUrl =
        import.meta.env.VITE_API_URL || "https://ebibliospace.com/api";
      xhr.open(
        book ? "PUT" : "POST",
        `${baseUrl}/books${book ? `/${book.id}` : ""}`
      );
      xhr.setRequestHeader(
        "Authorization",
        `Bearer ${localStorage.getItem("token")}`
      );
      xhr.send(submitData);

      const response = await promise;

      // Check if we need to upload PDF separately
      if (response && response.book && response.pdf_status === 'pending' && pdfFile) {
        // Book was created but PDF needs to be uploaded separately
        showSnackbar(
          t("admin.bookManagement.book.messages.createdUploadingPdf"),
          "info"
        );
        
        try {
          // Create FormData for PDF upload
          const pdfData = new FormData();
          pdfData.append("pdf", pdfFile);
          
          // Upload PDF file separately
          const uploadXhr = new XMLHttpRequest();
          const uploadPromise = new Promise((resolve, reject) => {
            uploadXhr.upload.onprogress = (event) => {
              if (event.lengthComputable) {
                const progress = Math.round((event.loaded * 100) / event.total);
                setUploadProgress(progress);
              }
            };
            
            uploadXhr.onload = () => {
              if (uploadXhr.status >= 200 && uploadXhr.status < 300) {
                resolve(JSON.parse(uploadXhr.response));
              } else {
                reject(new Error(`HTTP Error: ${uploadXhr.status}`));
              }
            };
            
            uploadXhr.onerror = () => reject(new Error("Network Error"));
          });
          
          uploadXhr.open("POST", `${baseUrl}/books/${response.book.id}/upload-file`);
          uploadXhr.setRequestHeader(
            "Authorization",
            `Bearer ${localStorage.getItem("token")}`
          );
          uploadXhr.send(pdfData);
          
          await uploadPromise;
          showSnackbar(
            t("admin.bookManagement.book.messages.pdfUploadSuccess"),
            "success"
          );
        } catch (error) {
          console.error("PDF upload error:", error);
          showSnackbar(
            t("admin.bookManagement.book.messages.pdfUploadError", {
              error: error.message || t("common.errors.generic"),
            }),
            "warning"
          );
          // Note: We continue with form submission since the book was created successfully
        }
      } else {
        // Normal success message
        showSnackbar(
          book
            ? t("admin.bookManagement.book.messages.updateSuccess")
            : t("admin.bookManagement.book.messages.createSuccess"),
          "success"
        );
      }
      
      onSubmit();
    } catch (error) {
      console.error("Book operation error:", error);
      setIsUploading(false);
      setUploadProgress(0);
      showSnackbar(
        t("admin.bookManagement.book.messages.uploadError", {
          error: error.message || t("common.errors.generic"),
        }),
        "error"
      );
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t("admin.bookManagement.book.form.title")}
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t("admin.bookManagement.book.form.author")}
            name="author"
            value={formData.author}
            onChange={handleChange}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>
              {t("admin.bookManagement.book.form.category")}
            </InputLabel>
            <Select
              name="category_id"
              value={formData.category_id}
              onChange={handleChange}
              label={t("admin.bookManagement.book.form.category")}
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {getLocalizedCategoryName(category.name, i18n.language)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t("admin.bookManagement.book.form.isbn")}
            name="isbn"
            value={formData.isbn}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t("admin.bookManagement.book.form.publicationYear")}
            name="publication_year"
            type="number"
            value={formData.publication_year}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t("admin.bookManagement.book.form.description")}
            name="description"
            value={formData.description}
            onChange={handleChange}
            multiline
            rows={4}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Button
            component="label"
            variant="outlined"
            startIcon={<UploadIcon />}
            fullWidth
          >
            {t("admin.bookManagement.book.form.uploadCover")}
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => handleFileChange(e, "cover")}
            />
          </Button>
          {coverFile && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              {t("admin.bookManagement.book.form.selected", {
                filename: coverFile.name,
              })}
            </Typography>
          )}

          {/* Cover Security Scan Results */}
          {isScanningCover && (
            <Paper elevation={3} sx={{ p: 1.5, mt: 2, bgcolor: 'primary.light', borderRadius: 2, border: 1, borderColor: 'primary.main' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <CircularProgress size={24} thickness={4} sx={{ color: 'primary.dark' }} />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Shield sx={{ fontSize: 14, color: 'primary.dark' }} />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="primary.dark" fontWeight={700}>
                    🔍 Scanning Cover
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    Analyzing for viruses...
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}

          {coverSecurity && !coverSecurity.threat && coverSecurity.scanned && (
            <Paper
              elevation={3}
              sx={{
                p: 1.5,
                mt: 2,
                bgcolor: 'success.light',
                borderRadius: 2,
                border: 1,
                borderColor: 'success.main',
                animation: 'fadeIn 0.5s ease-in',
                '@keyframes fadeIn': {
                  from: { opacity: 0, transform: 'scale(0.9)' },
                  to: { opacity: 1, transform: 'scale(1)' }
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <CheckCircle
                    sx={{
                      color: 'success.dark',
                      fontSize: 22,
                      animation: 'checkBounce 0.6s ease-out',
                      '@keyframes checkBounce': {
                        '0%': { transform: 'scale(0)' },
                        '50%': { transform: 'scale(1.2)' },
                        '100%': { transform: 'scale(1)' }
                      }
                    }}
                  />
                  <Verified
                    sx={{
                      position: 'absolute',
                      bottom: -2,
                      right: -2,
                      color: 'success.main',
                      fontSize: 12,
                      bgcolor: 'white',
                      borderRadius: '50%',
                      animation: 'verifiedPop 0.4s ease-out 0.5s both',
                      '@keyframes verifiedPop': {
                        '0%': { transform: 'scale(0)', opacity: 0 },
                        '50%': { transform: 'scale(1.3)' },
                        '100%': { transform: 'scale(1)', opacity: 1 }
                      }
                    }}
                  />
                </Box>
                <Box flex={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="success.dark" fontWeight={700}>
                      ✅ Cover is Clean
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: 'success.main',
                        color: 'white',
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 1,
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        animation: 'slideIn 0.3s ease-out 0.6s both',
                        '@keyframes slideIn': {
                          from: { transform: 'translateX(-10px)', opacity: 0 },
                          to: { transform: 'translateX(0)', opacity: 1 }
                        }
                      }}
                    >
                      VERIFIED
                    </Box>
                  </Box>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    No threats detected
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}

          {coverSecurity && !coverSecurity.scannerAvailable && (
            <Paper
              elevation={2}
              sx={{
                p: 1.5,
                mt: 2,
                bgcolor: 'warning.light',
                borderRadius: 2,
                border: 1,
                borderColor: 'warning.main',
                animation: 'fadeIn 0.5s ease-in',
                '@keyframes fadeIn': {
                  from: { opacity: 0, transform: 'scale(0.9)' },
                  to: { opacity: 1, transform: 'scale(1)' }
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Warning
                  sx={{
                    color: 'warning.dark',
                    fontSize: 22,
                    animation: 'pulse 0.8s ease-in-out',
                    '@keyframes pulse': {
                      '0%': { transform: 'scale(1)' },
                      '50%': { transform: 'scale(1.1)' },
                      '100%': { transform: 'scale(1)' }
                    }
                  }}
                />
                <Box>
                  <Typography variant="caption" color="warning.dark" fontWeight={700}>
                    ⚠️ Scanner Unavailable
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    ClamAV not installed
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}

          {coverSecurity && coverSecurity.threat && (
            <Paper elevation={2} sx={{ p: 1.5, mt: 2, bgcolor: 'error.light', border: 2, borderColor: 'error.main', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ErrorIcon sx={{ color: 'error.dark', fontSize: 20 }} />
                <Box>
                  <Typography variant="caption" color="error.dark" fontWeight={700}>
                    ⚠️ THREAT DETECTED
                  </Typography>
                  {coverSecurity.threatsDetected && coverSecurity.threatsDetected.map((threat, idx) => (
                    <Typography key={idx} variant="caption" display="block" color="error.dark">
                      • {threat}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Paper>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <Button
            component="label"
            variant="outlined"
            startIcon={<UploadIcon />}
            fullWidth
            color={!book && !pdfFile ? "error" : "primary"}
          >
            {t("admin.bookManagement.book.form.uploadPdf")}
            <input
              type="file"
              hidden
              accept=".pdf"
              onChange={(e) => handleFileChange(e, "pdf")}
            />
          </Button>
          {pdfFile && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              {t("admin.bookManagement.book.form.selected", {
                filename: pdfFile.name,
              })}
            </Typography>
          )}

          {/* PDF Security Scan Results */}
          {isScanningPdf && (
            <Paper elevation={3} sx={{ p: 1.5, mt: 2, bgcolor: 'primary.light', borderRadius: 2, border: 1, borderColor: 'primary.main' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <CircularProgress size={24} thickness={4} sx={{ color: 'primary.dark' }} />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Shield sx={{ fontSize: 14, color: 'primary.dark' }} />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="primary.dark" fontWeight={700}>
                    🔍 Scanning PDF
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    Analyzing for viruses...
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}

          {pdfSecurity && !pdfSecurity.threat && pdfSecurity.scanned && (
            <Paper
              elevation={3}
              sx={{
                p: 1.5,
                mt: 2,
                bgcolor: 'success.light',
                borderRadius: 2,
                border: 1,
                borderColor: 'success.main',
                animation: 'fadeIn 0.5s ease-in',
                '@keyframes fadeIn': {
                  from: { opacity: 0, transform: 'scale(0.9)' },
                  to: { opacity: 1, transform: 'scale(1)' }
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <CheckCircle
                    sx={{
                      color: 'success.dark',
                      fontSize: 22,
                      animation: 'checkBounce 0.6s ease-out',
                      '@keyframes checkBounce': {
                        '0%': { transform: 'scale(0)' },
                        '50%': { transform: 'scale(1.2)' },
                        '100%': { transform: 'scale(1)' }
                      }
                    }}
                  />
                  <Verified
                    sx={{
                      position: 'absolute',
                      bottom: -2,
                      right: -2,
                      color: 'success.main',
                      fontSize: 12,
                      bgcolor: 'white',
                      borderRadius: '50%',
                      animation: 'verifiedPop 0.4s ease-out 0.5s both',
                      '@keyframes verifiedPop': {
                        '0%': { transform: 'scale(0)', opacity: 0 },
                        '50%': { transform: 'scale(1.3)' },
                        '100%': { transform: 'scale(1)', opacity: 1 }
                      }
                    }}
                  />
                </Box>
                <Box flex={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="success.dark" fontWeight={700}>
                      ✅ PDF is Clean
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: 'success.main',
                        color: 'white',
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 1,
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        animation: 'slideIn 0.3s ease-out 0.6s both',
                        '@keyframes slideIn': {
                          from: { transform: 'translateX(-10px)', opacity: 0 },
                          to: { transform: 'translateX(0)', opacity: 1 }
                        }
                      }}
                    >
                      VERIFIED
                    </Box>
                  </Box>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    No threats detected
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}

          {pdfSecurity && !pdfSecurity.scannerAvailable && (
            <Paper
              elevation={2}
              sx={{
                p: 1.5,
                mt: 2,
                bgcolor: 'warning.light',
                borderRadius: 2,
                border: 1,
                borderColor: 'warning.main',
                animation: 'fadeIn 0.5s ease-in',
                '@keyframes fadeIn': {
                  from: { opacity: 0, transform: 'scale(0.9)' },
                  to: { opacity: 1, transform: 'scale(1)' }
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Warning
                  sx={{
                    color: 'warning.dark',
                    fontSize: 22,
                    animation: 'pulse 0.8s ease-in-out',
                    '@keyframes pulse': {
                      '0%': { transform: 'scale(1)' },
                      '50%': { transform: 'scale(1.1)' },
                      '100%': { transform: 'scale(1)' }
                    }
                  }}
                />
                <Box>
                  <Typography variant="caption" color="warning.dark" fontWeight={700}>
                    ⚠️ Scanner Unavailable
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    ClamAV not installed
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}

          {pdfSecurity && pdfSecurity.threat && (
            <Paper elevation={2} sx={{ p: 1.5, mt: 2, bgcolor: 'error.light', border: 2, borderColor: 'error.main', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ErrorIcon sx={{ color: 'error.dark', fontSize: 20 }} />
                <Box>
                  <Typography variant="caption" color="error.dark" fontWeight={700}>
                    ⚠️ THREAT DETECTED
                  </Typography>
                  {pdfSecurity.threatsDetected && pdfSecurity.threatsDetected.map((threat, idx) => (
                    <Typography key={idx} variant="caption" display="block" color="error.dark">
                      • {threat}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Paper>
          )}
        </Grid>

      </Grid>
      <Box sx={{ mt: 3 }}>
        {isUploading && (
          <Box sx={{ width: "100%", mb: 2 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <Typography
              variant="body2"
              color="textSecondary"
              align="center"
              sx={{ mt: 1 }}
            >
              {t("admin.bookManagement.book.form.uploading", {
                progress: uploadProgress,
              })}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button onClick={onCancel} disabled={isUploading}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="contained" disabled={isUploading}>
            {isUploading
              ? t("common.upload.uploading")
              : book
              ? t("common.update")
              : t("common.create")}
          </Button>
        </Box>
      </Box>
    </form>
  );
};

const BookManagement = () => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const { showSnackbar } = useSnackbar();
  const { t, i18n } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [selectedBookForDownload, setSelectedBookForDownload] = useState(null);
  const [userSelectedCover, setUserSelectedCover] = useState(false);

  // Search and filter states
  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState({
    search: "",
    category: "all",
    sort: "title",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
  });
  const [anchorEl, setAnchorEl] = useState(null);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getAll();
      const data = response.data?.categories || response.data || [];
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      showSnackbar(t("admin.bookManagement.errors.loadCategories"), "error");
    }
  };

  const fetchBooks = useCallback(async () => {
    try {
      setLoading(true);
      const queryParams = {
        page: pagination.page,
        limit: 9,
        sort: filters.sort,
        category: filters.category !== "all" ? filters.category : undefined,
        search: filters.search || undefined,
      };

      const response = await booksAPI.getAll(queryParams);

      if (response.data) {
        setBooks(response.data.books || []);
        setPagination((prev) => ({
          ...prev,
          totalPages: response.data.totalPages || 1,
        }));
      }
    } catch (error) {
      showSnackbar(t("admin.bookManagement.errors.loadBooks"), "error");
      console.error("Error fetching books:", error);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, showSnackbar]);

  useEffect(() => {
    fetchBooks();
    fetchCategories();
  }, [fetchBooks]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchInput }));
      setPagination((prev) => ({ ...prev, page: 1 }));
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleFilterChange = (name, value) => {
    if (name === "search") {
      setSearchInput(value);
    } else {
      setFilters((prev) => ({ ...prev, [name]: value }));
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenDialog = (book = null) => {
    setSelectedBook(book);
    setOpenDialog(true);
    setUserSelectedCover(false); // Always start with false to allow auto-generation
  };

  const handleCloseDialog = () => {
    setSelectedBook(null);
    setOpenDialog(false);
    setUserSelectedCover(false);
    // Clear security scan results
    setPdfSecurity(null);
    setCoverSecurity(null);
    setIsScanningPdf(false);
    setIsScanningCover(false);
  };

  const handleDeleteBook = async (bookId) => {
    if (
      window.confirm(t("admin.bookManagement.book.confirmations.deleteBook"))
    ) {
      try {
        await booksAPI.delete(bookId);
        showSnackbar(
          t("admin.bookManagement.book.messages.deleteSuccess"),
          "success"
        );
        fetchBooks();
      } catch (error) {
        showSnackbar(t("admin.bookManagement.book.errors.deleteBook"), "error");
      }
    }
  };

  const handleSubmit = async () => {
    try {
      handleCloseDialog();
      setUserSelectedCover(false); // Reset the flag
      await fetchBooks();
    } catch (error) {
      console.error("Error in submit:", error);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <CustomContainer>
      <Box sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label={t("admin.bookManagement.tabs.books")} />
            <Tab label={t("admin.bookManagement.tabs.accessRequests")} />
          </Tabs>
        </Box>

        {/* Books Management Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box
            sx={{
              mb: 4,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h4">
              {t("admin.bookManagement.title")}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              {t("admin.bookManagement.addBook")}
            </Button>
          </Box>

          {/* Filters */}
          <Box sx={{ mb: 4 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  placeholder={t("admin.bookManagement.search.placeholder")}
                  value={searchInput}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon color="action" />,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  select
                  fullWidth
                  value={filters.category}
                  onChange={(e) =>
                    handleFilterChange("category", e.target.value)
                  }
                  label={t("admin.bookManagement.filters.category")}
                >
                  <MenuItem value="all">
                    {t("admin.bookManagement.filters.allCategories")}
                  </MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {getLocalizedCategoryName(category.name, i18n.language)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  endIcon={<FilterIcon />}
                  onClick={handleMenuOpen}
                >
                  {t("admin.bookManagement.filters.sort")}
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Sort Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem
              onClick={() => {
                handleFilterChange("sort", "title");
                handleMenuClose();
              }}
            >
              {t("admin.bookManagement.filters.sortOptions.byTitle")}
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleFilterChange("sort", "author");
                handleMenuClose();
              }}
            >
              {t("admin.bookManagement.filters.sortOptions.byAuthor")}
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleFilterChange("sort", "recent");
                handleMenuClose();
              }}
            >
              {t("admin.bookManagement.filters.sortOptions.recentlyAdded")}
            </MenuItem>
          </Menu>

          {books.length === 0 ? (
            <Alert severity="info">
              {filters.search
                ? t("admin.bookManagement.alerts.noMatchingBooks")
                : t("admin.bookManagement.alerts.noBooks")}
            </Alert>
          ) : (
            <Grid container spacing={3}>
              {books.map((book) => (
                <Grid item key={book.id} xs={12} sm={6} md={4}>
                  <Card>
                    <Box sx={{ position: "relative", height: 200 }}>
                      <LazyImage
                        src={getCoverImageUrl(book.cover_image_path)}
                        alt={book.title}
                        sx={{
                          height: "100%",
                          width: "100%",
                        }}
                      />
                    </Box>
                    <CardContent>
                      <Typography variant="h6" noWrap title={book.title}>
                        {book.title}
                      </Typography>
                      <Typography color="textSecondary" noWrap>
                        {book.author}
                      </Typography>
                      {book.category && (
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                        >
                          {t("admin.bookManagement.book.category")}:{" "}
                          {getLocalizedCategoryName(
                            book.category.name,
                            i18n.language
                          )}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        display="block"
                      >
                        {t("admin.bookManagement.book.isbn")}:{" "}
                        {book.isbn || t("common.notAvailable")}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <IconButton
                        onClick={() => handleOpenDialog(book)}
                        color="primary"
                        title={t("admin.bookManagement.actions.edit")}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteBook(book.id)}
                        color="error"
                        title={t("admin.bookManagement.actions.delete")}
                      >
                        <DeleteIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          setSelectedBookForDownload(book);
                          setDownloadDialogOpen(true);
                        }}
                        color="primary"
                        title={t("admin.bookManagement.actions.download")}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <Box
              sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 2 }}
            >
              <Pagination
                count={pagination.totalPages}
                page={pagination.page}
                onChange={(_, page) =>
                  setPagination((prev) => ({ ...prev, page }))
                }
                color="primary"
              />
            </Box>
          )}
        </TabPanel>

        {/* Access Requests Tab */}
        <TabPanel value={activeTab} index={1}>
          <AccessRequestManager />
        </TabPanel>
      </Box>

      {/* Book Form Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedBook
            ? t("admin.bookManagement.dialog.editBook")
            : t("admin.bookManagement.dialog.addBook")}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <BookForm
              book={selectedBook}
              onSubmit={handleSubmit}
              onCancel={handleCloseDialog}
              userSelectedCover={userSelectedCover}
              onUserSelectedCoverChange={setUserSelectedCover}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Download Dialog */}
      <DownloadDialog
        open={downloadDialogOpen}
        book={selectedBookForDownload}
        onClose={() => {
          setDownloadDialogOpen(false);
          setSelectedBookForDownload(null);
        }}
      />
    </CustomContainer>
  );
};

export default BookManagement;
